import { useMemo } from 'react';
import { useStoreThirdPartyIntegration } from '../../../store/storeThirdPartyIntegration';
import type { IntegrationCategory } from '../../ThirdPartyIntegration/categories';
import type { IntegrationProviderConfig } from '../../ThirdPartyIntegration/types';

interface CatalogFilters {
  search: string;
  category: IntegrationCategory | 'all';
}

interface CatalogResult {
  connected: IntegrationProviderConfig[];
  browse: IntegrationProviderConfig[];
  totalConnectionsByProvider: Record<string, number>;
}

export function useIntegrationCatalog(
  providers: IntegrationProviderConfig[],
  filters: CatalogFilters,
): CatalogResult {
  const connectionsByProvider = useStoreThirdPartyIntegration(
    state => state.connectionsByProvider,
  );

  return useMemo(() => {
    const totals: Record<string, number> = {};
    for (const provider of providers) {
      totals[provider.id] = connectionsByProvider[provider.id]?.length ?? 0;
    }

    const normalizedQuery = filters.search.trim().toLowerCase();
    const matchesQuery = (provider: IntegrationProviderConfig): boolean => {
      if (!normalizedQuery) return true;
      const haystack = [
        provider.name,
        provider.id,
        provider.description ?? '',
        ...(provider.keywords ?? []),
        ...(provider.category ?? []),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    };

    const matchesCategory = (provider: IntegrationProviderConfig): boolean => {
      if (filters.category === 'all') return true;
      return (provider.category ?? []).includes(filters.category);
    };

    const filtered = providers.filter(
      provider => matchesQuery(provider) && matchesCategory(provider),
    );

    const byPriority = (
      a: IntegrationProviderConfig,
      b: IntegrationProviderConfig,
    ) => {
      const pa = a.priority ?? 0;
      const pb = b.priority ?? 0;
      if (pa !== pb) return pb - pa;
      return a.name.localeCompare(b.name);
    };

    const connected = filtered
      .filter(provider => (totals[provider.id] ?? 0) > 0)
      .sort(byPriority);
    const browse = filtered
      .filter(provider => (totals[provider.id] ?? 0) === 0)
      .sort(byPriority);

    return {
      connected,
      browse,
      totalConnectionsByProvider: totals,
    };
  }, [providers, filters.search, filters.category, connectionsByProvider]);
}
